import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import i18n from '@/i18n';
import type { SessionFormat, StudySession } from '@/types/api';

// ── Sessions ─────────────────────────────────────────────────────────────────

export function useSessions(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'sessions'],
    queryFn: () =>
      apiFetch<{ sessions: StudySession[]; count: number }>(`/children/${childId}/sessions`).then(
        (r) => r.sessions,
      ),
  });
}

export function useLogSession(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      subject: string;
      topic: string;
      format: SessionFormat;
      duration_minutes: number;
      note?: string;
    }) =>
      apiFetch<{ session_id: string; streak_count: number }>(`/children/${childId}/sessions`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['child', childId, 'sessions'] });
      qc.invalidateQueries({ queryKey: ['child', childId, 'weekly-progress'] });
    },
  });
}

// ── AI tools ─────────────────────────────────────────────────────────────────

export interface Flashcard {
  question: string;
  answer: string;
}

export function useGenerateFlashcards() {
  return useMutation({
    mutationFn: (body: { subject: string; topic: string; count?: number }) =>
      apiFetch<{ flashcards: Flashcard[] }>('/ai/flashcards', {
        method: 'POST',
        body: JSON.stringify({ ...body, lang: i18n.language.slice(0, 2) }),
      }),
  });
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string;
}

export function useGenerateQuiz() {
  return useMutation({
    mutationFn: (body: { subject: string; topic: string; count?: number }) =>
      apiFetch<{ questions: QuizQuestion[] }>('/ai/quiz', {
        method: 'POST',
        body: JSON.stringify({ ...body, lang: i18n.language.slice(0, 2) }),
      }),
  });
}

export function useExplain() {
  return useMutation({
    mutationFn: (body: { subject: string; topic: string }) =>
      apiFetch<{ explanation: string }>('/ai/explain', {
        method: 'POST',
        body: JSON.stringify({ ...body, lang: i18n.language.slice(0, 2) }),
      }),
  });
}

// ── Weekly progress ───────────────────────────────────────────────────────────

export interface DayProgress {
  date: string;
  total_minutes: number;
  session_count: number;
}

export function useWeeklyProgress(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'weekly-progress'],
    queryFn: () =>
      apiFetch<{ days: DayProgress[]; total_minutes: number; streak_count: number }>(
        `/children/${childId}/weekly-progress`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Goal ─────────────────────────────────────────────────────────────────────

export function useGoal(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'goal'],
    queryFn: () =>
      apiFetch<{ child_id: string; weekly_goal_minutes: number | null }>(
        `/children/${childId}/goal`,
      ),
  });
}

export function useSetGoal(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekly_goal_minutes: number) =>
      apiFetch<{ child_id: string; weekly_goal_minutes: number }>(`/children/${childId}/goal`, {
        method: 'PUT',
        body: JSON.stringify({ weekly_goal_minutes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'goal'] }),
  });
}

// ── Commitments ───────────────────────────────────────────────────────────────

export type CommitmentType = 'sport' | 'music' | 'work' | 'other';

export interface Commitment {
  commitment_id: string;
  label: string;
  type: CommitmentType;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

export function useCommitments(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'commitments'],
    queryFn: () =>
      apiFetch<{ commitments: Commitment[] }>(`/children/${childId}/commitments`).then(
        (r) => r.commitments,
      ),
  });
}

export function useAddCommitment(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<Commitment, 'commitment_id'>) =>
      apiFetch<{ commitment_id: string }>(`/children/${childId}/commitments`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'commitments'] }),
  });
}

export function useDeleteCommitment(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commitmentId: string) =>
      apiFetch<void>(`/children/${childId}/commitments/${commitmentId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'commitments'] }),
  });
}

// ── Exams ─────────────────────────────────────────────────────────────────────

export interface Exam {
  exam_id: string;
  subject: string;
  name: string;
  date: string;
}

export function useExams(childId: string) {
  return useQuery({
    queryKey: ['child', childId, 'exams'],
    queryFn: () =>
      apiFetch<{ exams: Exam[] }>(`/children/${childId}/exams`).then((r) => r.exams),
  });
}

export function useAddExam(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { subject: string; name: string; date: string }) =>
      apiFetch<{ exam_id: string }>(`/children/${childId}/exams`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'exams'] }),
  });
}

export function useDeleteExam(childId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (examId: string) =>
      apiFetch<void>(`/children/${childId}/exams/${examId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['child', childId, 'exams'] }),
  });
}

// ── Session limit ─────────────────────────────────────────────────────────────

const MONTHLY_LIMIT = 20;

export function useSessionLimit(childId: string) {
  const { data: sessions } = useSessions(childId);
  if (!sessions) return { used: 0, remaining: MONTHLY_LIMIT, limit: MONTHLY_LIMIT };

  const now = new Date();
  const used = sessions.filter((s) => {
    const d = new Date(s.created_at);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;

  return { used, remaining: Math.max(0, MONTHLY_LIMIT - used), limit: MONTHLY_LIMIT };
}
